import { createPortal } from "react-dom";
import { ChevronUp } from "lucide-react";
import { Button } from "@/components/atoms/button";
import { cn } from "@/lib/utils";
import { useEditorDrawerNavigation } from "@/features/sparxsEditor/contexts/EditorDrawerNavigationContext";
import {
  getReturnToPreviewAriaLabel,
  getScrollToEditorHeaderAriaLabel,
} from "./editorConstants";
import { useEditorTabPanelOverlayRoot } from "@/features/sparxsEditor/hooks/editor/useEditorTabPanelOverlayRoot";

export type EditorDrawerCloseButtonVariant = "inline" | "floating";

export interface EditorDrawerCloseButtonProperties {
  variant: EditorDrawerCloseButtonVariant;
}

export const EditorDrawerCloseButton = ({
  variant,
}: EditorDrawerCloseButtonProperties) => {
  const navigation = useEditorDrawerNavigation();
  if (navigation == null) {
    return null;
  }

  const { closeAndScrollToPreview, scrollDrawerToTop, headerVisible } =
    navigation;
  const isFloating = variant === "floating";

  if (isFloating && headerVisible) {
    return null;
  }
  if (!isFloating && !headerVisible) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="consoleSurface"
      size="icon"
      aria-label={
        isFloating
          ? getScrollToEditorHeaderAriaLabel()
          : getReturnToPreviewAriaLabel()
      }
      onClick={isFloating ? scrollDrawerToTop : closeAndScrollToPreview}
      className={cn(
        "shrink-0",
        isFloating
          ? cn(
              `pointer-events-auto size-11 rounded-full shadow-none`,
              "data-[state=open]:duration-200 data-[state=open]:animate-in",
              `
                data-[state=open]:fade-in
                data-[state=open]:slide-in-from-right-full
              `,
            )
          : "size-9",
      )}
      data-editor={isFloating ? "drawer-floating-close" : "drawer-inline-close"}
      data-state="open"
    >
      <ChevronUp className="size-4" />
    </Button>
  );
};

export const EditorDrawerFloatingClosePortal = () => {
  const navigation = useEditorDrawerNavigation();
  const showFloating = navigation != null && !navigation.headerVisible;
  const { overlayRoot, overlayStyle } = useEditorTabPanelOverlayRoot(
    navigation?.drawerAnchorRef ?? null,
    showFloating,
  );

  if (!showFloating || overlayRoot == null) {
    return null;
  }

  return createPortal(
    <div style={overlayStyle} data-editor="drawer-floating-close-portal">
      <div className="pointer-events-none absolute inset-0">
        <div className="pointer-events-auto absolute right-4 bottom-4">
          <EditorDrawerCloseButton variant="floating" />
        </div>
      </div>
    </div>,
    overlayRoot,
  );
};
